import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Navigate } from 'react-router';
import { Alert, Box, Button, Container, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import type { Certification, CertMember } from 'src/types/Certification';
import type {
  QIT,
  QuestionGroup,
  QuestionnaireSchema,
  Section,
} from 'src/components/WSCertificationSummary/types/QuestionnaireSchemaTypes';
import type { QuestionnaireAnswersMap } from 'src/services/RenterQuestionnaire.service';
import {
  fetchQuestionnaireSchema,
  fetchQuestionnaireAnswers,
} from 'src/services/RenterQuestionnaire.service';
import {
  setSchema,
  setSchemaLoading,
  setSchemaFailed,
  setAnswers,
  setAnswersLoading,
  setAnswersFailed,
  setQuestionnaireVisibility,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import { setToolbarOffset } from 'src/redux/slices/NewRenterInterface/NewRentersInterfaceUIManagementSlice';
import { fadeStyle } from 'src/components/NewRenterInterface/constants';
import LoadingScreen from 'src/components/LoadingScreen';
import QuestionnaireSection from './QuestionnaireSection';
import UnansweredFilter from './UnansweredFilter';
import { useConditionalVisibility } from './hooks/useConditionalVisibility';
import { useQuestionnaireFocus } from './hooks/useQuestionnaireFocus';
import { useTransferGuard } from './hooks/useTransferGuard';
import CertificationMemberHeader from 'src/components/NewRenterInterface/CertificationMemberHeader';

function isQitUnanswered(
  qit: QIT,
  answers: QuestionnaireAnswersMap,
  surveyId: number
): boolean {
  const qitAnswers = answers[qit.id];
  if (!qitAnswers) {
    return true;
  }
  const surveyAnswers = qitAnswers[surveyId];
  if (!surveyAnswers) {
    return true;
  }
  for (const answer of Object.values(surveyAnswers)) {
    if (
      answer.text ||
      answer.text_area ||
      (answer.question_choices && answer.question_choices.length > 0) ||
      (answer.files && answer.files.length > 0)
    ) {
      return false;
    }
  }
  return true;
}

function getMemberForSurvey(
  cert: Certification | undefined,
  surveyId: number
): CertMember | null {
  if (!cert?.cert_household?.cert_members) {
    return null;
  }
  return (
    cert.cert_household.cert_members.find((m) =>
      m.surveys.includes(surveyId)
    ) ?? null
  );
}

function qitMatchesMemberType(qit: QIT, memberTypeId: number | null): boolean {
  if (qit.member_types.length === 0) {
    return true;
  }
  if (memberTypeId === null) {
    return true;
  }
  return qit.member_types.includes(memberTypeId);
}

function groupMatchesMemberType(
  group: QuestionGroup,
  memberTypeId: number | null
): boolean {
  if (group.householdmember_type.length === 0) {
    return true;
  }
  if (memberTypeId === null) {
    return true;
  }
  return group.householdmember_type.includes(memberTypeId);
}

const RenterQuestionnairePage: FC = () => {
  const { certificationId, surveyId } = useParams<{
    certificationId: string;
    surveyId: string;
  }>();
  const dispatch = useDispatch();

  const certId = certificationId ? Number(certificationId) : NaN;
  const sId = surveyId ? Number(surveyId) : NaN;

  const { isChecking, shouldRender } = useTransferGuard(
    Number.isFinite(sId) ? sId : undefined
  );

  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];

  const cert = useMemo(
    () =>
      Number.isFinite(certId)
        ? certifications.find((c) => c.id === certId)
        : undefined,
    [certifications, certId]
  );

  const activeMember = useMemo(
    () => getMemberForSurvey(cert, sId),
    [cert, sId]
  );

  const memberTypeId = useMemo(
    () => activeMember?.member_type_id ?? null,
    [activeMember]
  );

  const configId = cert?.certification_config ?? 0;

  const schema = useSelector(
    (state: RootState) => state.newRenterInterface.schemas[configId]
  ) as QuestionnaireSchema | undefined;

  const schemaLoading = useSelector(
    (state: RootState) => state.newRenterInterface.schemasLoading[configId]
  );

  const answers = useSelector(
    (state: RootState) => state.newRenterInterface.answers[certId]
  ) as QuestionnaireAnswersMap | undefined;

  const answersLoaded = useSelector(
    (state: RootState) => state.newRenterInterface.answersLoaded[certId]
  );

  const answersLoading = useSelector(
    (state: RootState) => state.newRenterInterface.answersLoading[certId]
  );

  const schemaFailed = useSelector(
    (state: RootState) => state.newRenterInterface.schemasFailed[configId]
  );

  const answersFailed = useSelector(
    (state: RootState) => state.newRenterInterface.answersFailed[certId]
  );

  useEffect(() => {
    if (!configId || schema || schemaLoading || schemaFailed) {
      return;
    }
    dispatch(setSchemaLoading({ configId, loading: true }));
    fetchQuestionnaireSchema(configId)
      .then((data) => {
        dispatch(setSchema({ configId, schema: data }));
      })
      .catch(() => {
        dispatch(setSchemaFailed({ configId, failed: true }));
        dispatch(setSchemaLoading({ configId, loading: false }));
      });
  }, [configId, schema, schemaLoading, schemaFailed, dispatch]);

  useEffect(() => {
    if (
      !certId ||
      !Number.isFinite(certId) ||
      answersLoaded ||
      answersLoading ||
      answersFailed
    ) {
      return;
    }
    dispatch(setAnswersLoading({ certId, loading: true }));
    fetchQuestionnaireAnswers(certId)
      .then((data) => {
        dispatch(setAnswers({ certId, data }));
      })
      .catch(() => {
        dispatch(setAnswersFailed({ certId, failed: true }));
        dispatch(setAnswersLoading({ certId, loading: false }));
      });
  }, [certId, answersLoaded, answersLoading, answersFailed, dispatch]);

  const [showOnlyUnanswered, setShowOnlyUnanswered] = useState(false);

  const allGroups = useMemo(
    () =>
      (schema?.question_groups ?? []).filter((g) =>
        groupMatchesMemberType(g, memberTypeId)
      ),
    [schema, memberTypeId]
  );
  const allQits = useMemo(
    () =>
      (schema?.qits ?? []).filter((q) => qitMatchesMemberType(q, memberTypeId)),
    [schema, memberTypeId]
  );

  const { visibleGroupIds, visibleQitIds, newlyRevealed, revealOrigin } =
    useConditionalVisibility(certId, sId, allGroups, allQits);

  const { isFocusMode, clearFocus, getVisibleQitIds } = useQuestionnaireFocus();

  const effectiveVisibleQitIds = useMemo(
    () => getVisibleQitIds(visibleQitIds, allQits),
    [getVisibleQitIds, visibleQitIds, allQits]
  );

  const filteredQitIds = useMemo(() => {
    if (!showOnlyUnanswered || !answers) {
      return effectiveVisibleQitIds;
    }
    const result = new Set<number>();
    for (const id of effectiveVisibleQitIds) {
      const qit = allQits.find((q) => q.id === id);
      if (
        qit &&
        (qit.necessity === 1 || qit.necessity === 2) &&
        isQitUnanswered(qit, answers, sId)
      ) {
        result.add(id);
      }
    }
    return result;
  }, [showOnlyUnanswered, effectiveVisibleQitIds, answers, allQits, sId]);

  useEffect(() => {
    if (!Number.isFinite(certId)) {
      return;
    }
    const originRecord: Record<
      number,
      { questionName: string; choiceName: string }
    > = {};
    for (const [key, value] of revealOrigin.entries()) {
      originRecord[key] = value;
    }
    dispatch(
      setQuestionnaireVisibility({
        certId,
        visibleGroupIds: [...visibleGroupIds],
        visibleQitIds: [...visibleQitIds],
        newlyRevealed: [...newlyRevealed],
        revealOrigin: originRecord,
        filteredQitIds: [...filteredQitIds],
        memberTypeId,
      })
    );
  }, [
    certId,
    visibleGroupIds,
    visibleQitIds,
    newlyRevealed,
    revealOrigin,
    filteredQitIds,
    memberTypeId,
    dispatch,
  ]);

  const sortedSections = useMemo(() => {
    if (!schema) {
      return [];
    }
    const minOrderBySection = new Map<number, number>();
    for (const qit of allQits) {
      const prev = minOrderBySection.get(qit.section_id);
      if (prev === undefined || qit.order < prev) {
        minOrderBySection.set(qit.section_id, qit.order);
      }
    }
    return [...schema.sections]
      .filter((s) => minOrderBySection.has(s.id))
      .sort(
        (a, b) =>
          (minOrderBySection.get(a.id) ?? 0) -
          (minOrderBySection.get(b.id) ?? 0)
      );
  }, [schema, allQits]);

  const [toolbarVisible, setToolbarVisible] = useState(true);
  const lastScrollTopRef = useRef(0);
  const toolbarHeightRef = useRef(0);
  const toolbarElRef = useRef<HTMLDivElement | null>(null);
  const toolbarIsStickyRef = useRef(false);

  const toolbarRef = useCallback(
    (el: HTMLDivElement | null) => {
      toolbarElRef.current = el;
      if (!el) {
        return;
      }
      const height = el.getBoundingClientRect().height;
      if (height !== toolbarHeightRef.current) {
        toolbarHeightRef.current = height;
        dispatch(setToolbarOffset(toolbarVisible ? height : 0));
      }
    },
    [toolbarVisible, dispatch]
  );

  useEffect(() => {
    dispatch(setToolbarOffset(toolbarVisible ? toolbarHeightRef.current : 0));
  }, [toolbarVisible, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setToolbarOffset(0));
    };
  }, [dispatch]);

  useEffect(() => {
    const scrollContainer = document.querySelector('main');
    if (!scrollContainer) {
      return;
    }

    const handleScroll = (): void => {
      const currentTop = scrollContainer.scrollTop;

      let sticky = toolbarIsStickyRef.current;
      if (toolbarElRef.current) {
        const rect = toolbarElRef.current.getBoundingClientRect();
        const mainRect = scrollContainer.getBoundingClientRect();
        sticky = rect.top <= mainRect.top;
        toolbarIsStickyRef.current = sticky;
      }

      if (!sticky || currentTop <= 0) {
        setToolbarVisible(true);
      } else if (currentTop < lastScrollTopRef.current) {
        setToolbarVisible(true);
      } else {
        setToolbarVisible(false);
      }
      lastScrollTopRef.current = currentTop;
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (!cert) {
    return <Navigate to="/renter" replace />;
  }

  if (isChecking) {
    return <LoadingScreen />;
  }

  if (!shouldRender) {
    return null; // Navigate is in progress
  }

  if (schemaFailed || answersFailed) {
    const handleRetry = (): void => {
      if (schemaFailed) {
        dispatch(setSchemaFailed({ configId, failed: false }));
      }
      if (answersFailed) {
        dispatch(setAnswersFailed({ certId, failed: false }));
      }
    };

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 480 }}>
          Failed to load the questionnaire. Please check your connection and try
          again.
        </Alert>
        <Button variant="contained" onClick={handleRetry}>
          Retry
        </Button>
      </Box>
    );
  }

  if (schemaLoading || !schema || !answersLoaded) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100%' }}>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Stack gap={2} sx={fadeStyle}>
          {/* Header: property/member info */}
          <CertificationMemberHeader
            propertyName={cert.property?.name ?? '—'}
            categoryName={cert.category?.name ?? '—'}
            memberName={
              activeMember
                ? `${activeMember.first_name} ${activeMember.last_name}`
                : undefined
            }
            memberTypeName={activeMember?.member_type_name}
          />

          {/* Toolbar: scroll-up-to-reveal sticky bar */}
          <Box
            ref={toolbarRef}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 4,
              transform: toolbarVisible ? 'translateY(0)' : 'translateY(-100%)',
              transition: 'transform 0.25s ease-in-out',
              backgroundColor: 'background.default',
              py: 1,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <UnansweredFilter
                showOnlyUnanswered={showOnlyUnanswered}
                onToggle={setShowOnlyUnanswered}
              />

              {isFocusMode && (
                <Button size="small" variant="outlined" onClick={clearFocus}>
                  Show all questions
                </Button>
              )}
            </Stack>
          </Box>

          {isFocusMode && (
            <Alert severity="info">Showing focused questions only</Alert>
          )}

          {sortedSections.map((section: Section) => (
            <QuestionnaireSection key={section.id} section={section} />
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default RenterQuestionnairePage;
